import classnames from 'classnames'
import { useId } from 'react'
import styles from './BarPattern.module.scss'

const PATTERN_WIDTH = 1452.366
const PATTERN_HEIGHT = 120

type Rect = [x: number, y: number, w: number, h: number]

// Pale dominant bars — tinted per variant via `currentColor`.
const blue2Rects: Rect[] = [
	[0, 0, 4, 48],
	[15.366, 52, 4, 48],
	[30.731, 104, 4, 16],
	[46.097, 84, 4, 16],
	[61.462, 88, 4, 32],
	[76.828, 88, 4, 32],
	[92.194, 88, 4, 32],
	[107.559, 88, 4, 32],
	[122.925, 88, 4, 32],
	[138.29, 88, 4, 32],
	[153.656, 104, 4, 16],
	[353.409, 103, 4, 17],
	[368.774, 84, 4, 16],
	[384.14, 84, 4, 16],
	[399.505, 67, 4, 16],
	[414.871, 68, 4, 32],
	[430.237, 68, 4, 32],
	[445.602, 68, 4, 32],
	[460.968, 68, 4, 32],
	[476.333, 68, 4, 32],
	[491.699, 32, 4, 32],
	[507.065, 48, 4, 32],
	[522.43, 68, 4, 32],
	[691.452, 104, 4, 16],
	[706.817, 88, 4, 32],
	[722.183, 88, 4, 32],
	[737.549, 88, 4, 32],
	[752.914, 88, 4, 32],
	[768.28, 88, 4, 32],
	[783.645, 68, 4, 32],
]

const gray1Rects: Rect[] = [
	[0, 52, 4, 48],
	[476.333, 104, 4, 16],
	[491.699, 68, 4, 32],
	[507.065, 84, 4, 16],
	[783.645, 104, 4, 16],
	[799.011, 84, 4, 16],
	[814.377, 84, 4, 16],
	[829.742, 68, 4, 32],
	[845.108, 84, 4, 16],
]

const gray2Rects: Rect[] = [
	[46.097, 104, 4, 16],
	[368.774, 104, 4, 16],
	[384.14, 104, 4, 16],
	[399.505, 87, 4, 33],
	[414.871, 104, 4, 16],
	[430.237, 104, 4, 16],
	[445.602, 104, 4, 16],
	[460.968, 104, 4, 16],
]

const accentRects: Rect[] = [
	[0, 104, 4, 16],
	[15.366, 104, 4, 16],
	[491.699, 104, 4, 16],
	[507.065, 104, 4, 16],
	[522.43, 104, 4, 16],
	[799.011, 104, 4, 16],
	[814.377, 104, 4, 16],
	[829.742, 104, 4, 16],
	[845.108, 104, 4, 16],
	[860.473, 104, 4, 16],
	[879.839, 104, 4, 16],
	[899.205, 104, 4, 16],
	[914.57, 104, 4, 16],
	[929.936, 104, 4, 16],
	[945.301, 104, 4, 16],
	[960.667, 104, 4, 16],
]

const dotXs = [
	169.021, 184.387, 199.753, 215.118, 230.484, 245.849, 261.215, 276.581,
	291.946, 307.312, 322.677, 338.043, 537.796, 553.161, 568.527, 583.893,
	599.258, 614.624, 629.989, 645.355, 660.721, 676.086, 976.033, 991.398,
	1006.764, 1022.129, 1037.495, 1052.861, 1068.226, 1083.592, 1098.957,
	1114.323, 1129.689, 1145.054, 1160.42, 1175.785, 1191.151, 1206.517, 1221.882,
	1237.248, 1252.613, 1267.979, 1283.345, 1298.71, 1314.076, 1329.441, 1344.807,
	1360.173, 1375.538, 1390.904, 1406.269, 1421.635, 1437.001,
]

export type BarPatternVariant = 'blue' | 'orange' | 'green' | 'red'

export type BarPatternProps = React.ComponentProps<'svg'> & {
	variant?: BarPatternVariant
}

export const BarPattern = ({
	className,
	variant = 'blue',
	...props
}: BarPatternProps) => {
	const patternId = useId()

	return (
		<svg
			className={classnames(
				styles.svg,
				{
					[styles.variantBlue]: variant === 'blue',
					[styles.variantOrange]: variant === 'orange',
					[styles.variantGreen]: variant === 'green',
					[styles.variantRed]: variant === 'red',
				},
				className,
			)}
			width="100%"
			height={PATTERN_HEIGHT}
			role="presentation"
			aria-hidden="true"
			{...props}
		>
			<defs>
				<pattern
					id={patternId}
					x="0"
					y="0"
					width={PATTERN_WIDTH}
					height={PATTERN_HEIGHT}
					patternUnits="userSpaceOnUse"
				>
					{dotXs.map((x, i) => (
						<rect
							key={`d-${i}`}
							x={x}
							y={116}
							width={4}
							height={4}
							fill="currentColor"
							className={styles.blue1}
						/>
					))}
					{blue2Rects.map(([x, y, w, h], i) => (
						<rect
							key={`a-${i}`}
							x={x}
							y={y}
							width={w}
							height={h}
							fill="currentColor"
							className={styles.blue2}
						/>
					))}
					{accentRects.map(([x, y, w, h], i) => (
						<rect
							key={`w-${i}`}
							x={x}
							y={y}
							width={w}
							height={h}
							fill="currentColor"
							className={styles.accent}
						/>
					))}
					{gray1Rects.map(([x, y, w, h], i) => (
						<rect
							key={`b-${i}`}
							x={x}
							y={y}
							width={w}
							height={h}
							fill="currentColor"
							className={styles.gray1}
						/>
					))}
					{gray2Rects.map(([x, y, w, h], i) => (
						<rect
							key={`g-${i}`}
							x={x}
							y={y}
							width={w}
							height={h}
							fill="currentColor"
							className={styles.gray2}
						/>
					))}
				</pattern>
			</defs>
			<rect width="100%" height={PATTERN_HEIGHT} fill={`url(#${patternId})`} />
		</svg>
	)
}
